import resizeService from '../services.common/resizeService';

const CONFETTI_CANVAS_NAME = 'confetti-canvas';

export default class ConfettiCanvas extends HTMLCanvasElement {
    constructor() {
        super();

        this._handleResize = this._handleResize.bind(this);
    }

    _setConfettiSize() {
        const cardWidth = document.querySelector('.ecard-preview').clientWidth;
        const width = Math.min(200, cardWidth / 3);
        const height = width;
        this.confettiSize = { width: width * this.devicePixelRatio, height: height * this.devicePixelRatio };
    }

    _setCanvasSize() {
        const canvasRect = this.getBoundingClientRect();
        this.width = canvasRect.width * this.devicePixelRatio;
        this.height = canvasRect.height * this.devicePixelRatio;
    }

    _drawConfetti(x, y, scale, scaleY, rotation, rotate, opacity) {
        this._context.save();
        this._context.translate(x, y);
        this._context.rotate(rotation);
        this._context.scale(scale, scaleY / 10);
        this._context.rotate(rotate);
        this._context.globalAlpha = opacity;
        this._context.drawImage(
            this._iconImage,
            -(this.confettiSize.width / 2),
            -(this.confettiSize.height / 2),
            this.confettiSize.width,
            this.confettiSize.height
        );
        this._context.restore();
    }

    _updateConfetti(confetti) {
        confetti.x += Math.cos(confetti.angle2D) * confetti.velocity + confetti.drift;
        confetti.y += Math.sin(confetti.angle2D) * confetti.velocity + confetti.gravity;
        confetti.wobble += 0.05;
        confetti.velocity *= confetti.decay;
        confetti.tiltAngle += 0.1;
        confetti.tiltSin = Math.sin(confetti.tiltAngle);
        confetti.tiltCos = Math.cos(confetti.tiltAngle);
        confetti.random = Math.random() + 5;
        confetti.wobbleX = confetti.x + 10 * confetti.scalar * Math.cos(confetti.wobble);
        confetti.wobbleY = confetti.y + 10 * confetti.scalar * Math.sin(confetti.wobble);

        const progress = confetti.tick++ / confetti.ticks;

        //const x1 = confetti.x + confetti.random * confetti.tiltCos;
        const y1 = confetti.y + confetti.random * confetti.tiltSin;
        //const x2 = confetti.wobbleX + confetti.random * confetti.tiltCos;
        const y2 = confetti.wobbleY + confetti.random * confetti.tiltSin;

        const opacity = confetti.fade ? Math.min(1, (1 - Math.sin((progress * Math.PI) / 2)) * 20) : 1;

        this._drawConfetti(
            confetti.x,
            confetti.y,
            //x2 - x1,
            confetti.scalar,
            y2 - y1,
            ((Math.PI / 2) * confetti.wobble * confetti.direction) / 2,

            confetti.rotate,
            opacity
        );

        return confetti.tick < confetti.ticks;
    }

    _animate(confettis) {
        let animatingConfettis = confettis.slice();

        const prom = new Promise((resolve) => {
            const update = () => {
                this._context.clearRect(0, 0, this.width, this.height);

                animatingConfettis = animatingConfettis.filter((confetti) => {
                    return this._updateConfetti(confetti);
                });

                if (animatingConfettis.length) {
                    requestAnimationFrame(update);
                } else {
                    this._context.clearRect(0, 0, this.width, this.height);
                    this._animationObj = null;

                    resolve();
                }
            };

            if (this._iconImage.complete) {
                requestAnimationFrame(update);
            } else {
                this._iconImage.onload = () => {
                    requestAnimationFrame(update);
                };
            }
        });

        return {
            addConfettis: function (confettis) {
                animatingConfettis = animatingConfettis.concat(confettis);

                return prom;
            },
            promise: prom,
        };
    }

    fire(options) {
        options = Object.assign(this._defaults, options);

        const confettis = [];
        const startX = this.width * options.origin.x;
        const startY = this.height * options.origin.y;
        const radAngle = options.angle * (Math.PI / 180);
        const radSpread = options.spread * (Math.PI / 180);

        let temp = options.confettiCount;

        while (temp--) {
            confettis.push({
                x: startX,
                y: startY,
                wobble: Math.random() * 10,
                velocity: (options.startVelocity * 0.5 + Math.random() * options.startVelocity) * this.devicePixelRatio,
                angle2D: -radAngle + (0.5 * radSpread - Math.random() * radSpread),
                tiltAngle: Math.random() * Math.PI,
                tick: 0,
                ticks: options.ticks,
                decay: options.decay,
                drift: options.drift,
                random: Math.random(),
                tiltSin: 0,
                tiltCos: 0,
                wobbleX: 0,
                wobbleY: 0,
                gravity: options.gravity * 3 * this.devicePixelRatio,
                scalar: Math.random() * (1 - 0.2) + 0.2,
                rotate: (Math.random() * 360 * Math.PI) / 180,
                direction: Math.round(Math.random()) * 2 - 1,
                fade: options.fade,
            });
        }

        confettis.sort((a, b) => (a.scalar > b.scalar ? 1 : b.scalar > a.scalar ? -1 : 0));

        if (this._animationObj) {
            return this._animationObj.addConfettis(confettis);
        }

        this._animationObj = this._animate(confettis);

        return this._animationObj.promise;
    }

    _handleResize() {
        this._setCanvasSize();
        this._setConfettiSize();
    }

    connectedCallback() {
        this._defaults = {
            confettiCount: 30,
            angle: 90,
            spread: 45,
            startVelocity: 80,
            decay: 0.9,
            gravity: 3,
            drift: 0,
            ticks: 200,
            x: 0.5,
            y: 0.5,
            scalar: 1,
            fade: true,
        };
        this._iconImage = new Image();
        this._iconImage.src = this.dataset.confettiCanvasSrc;
        this.devicePixelRatio = window.devicePixelRatio || 1;
        this._context = this.getContext('2d');
        this._animationObj;
        this._handleResize();
        resizeService.add(this._handleResize);
    }

    disconnectedCallback() {
        resizeService.remove(this._handleResize);
    }
}

customElements.define(CONFETTI_CANVAS_NAME, ConfettiCanvas, { extends: 'canvas' });
